import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Box, Typography, Avatar, IconButton, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from '@mui/material';
import { typoBase, thinBold } from '../styles/typography';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import { deleteTimesheet, getTimesheetsByCaseId } from '../redux/actions/timesheetActions';
import { useParams } from 'react-router-dom';

const TimesheetSummaryCard = ({ timesheets = [] }) => {
  const { t } = useTranslation('common');
  const dispatch = useDispatch();
  const { id } = useParams();
  const [hoveredTimesheetId, setHoveredTimesheetId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [timesheetToDelete, setTimesheetToDelete] = useState(null);
  // const formatDate = (dateString) => {
  //   const date = new Date(dateString);
  //   return date.toLocaleDateString('en-US', {
  //     year: 'numeric',
  //     month: 'short',
  //     day: 'numeric'
  //   });
  // };
  const formatDate = (dateString) => {
    if (!dateString) return '';
  
    const date = new Date(dateString);
  
    const formattedDate = date.toLocaleDateString(undefined, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).replaceAll('/', '-'); // Replace slashes with dashes
  
    // const formattedTime = date.toLocaleTimeString(undefined, {
    //   hour: '2-digit',
    //   minute: '2-digit',
    //   hour12: true,
    // });
  
    return `${formattedDate}`;
  };

  // Format duration (HH:MM)
  const formatDuration = (durationString) => {
    // Assuming durationString is in format like "01:30:00" (HH:MM:SS)
    if (!durationString) return "00:00";
    const parts = durationString.split(':');
    return `${parts[0]}:${parts[1]}`;
  };

  const handleDeleteClick = (timesheetId, e) => {
    e.stopPropagation();
    setTimesheetToDelete(timesheetId);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (timesheetToDelete) {
      try {
        await dispatch(deleteTimesheet(timesheetToDelete));
        await dispatch(getTimesheetsByCaseId(id));
      } catch (error) {
        console.error('Error deleting timesheet:', error);
      }
    }
    setDeleteDialogOpen(false);
    setTimesheetToDelete(null);
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setTimesheetToDelete(null);
  };

  return (
    <Box>
      {timesheets.length > 0 ? (
        timesheets.slice(0, 10).map((timesheet) => (
          <Box
            key={timesheet._id}
            display="flex"
            alignItems="center"
            onMouseEnter={() => setHoveredTimesheetId(timesheet._id)}
            onMouseLeave={() => setHoveredTimesheetId(null)}
            sx={{
              borderBottom: '1px solid #ECECEC',
              padding: '8px 0',
              '&:hover': {
                backgroundColor: '#ecf5fb',
              },
            }}
          >
            <AccessTimeIcon sx={{ color: 'rgb(139, 138, 138)', fontSize: '18px', marginRight: '10px', marginBottom: '17px' }} />
            <Box flex={1}>
              <Typography variant="body1" style={{ ...typoBase, fontSize: "10.5px" }}>
                {timesheet.description || timesheet.type}
              </Typography>
              <Typography variant="caption" style={{ color: '#777', fontSize: '11px' }}>
                {formatDate(timesheet.date)} • {formatDuration(timesheet.totalDuration)}
              </Typography>
            </Box>
            <Box display="flex" alignItems="center">
              <Typography 
                variant="body2" 
                style={{ 
                  ...thinBold, 
                  color: timesheet.isBilled ? '#84BC14' : '#555',
                  marginRight: '10px'
                }}
              >
                €{timesheet.totalAmount.toFixed(2)}
              </Typography>
              {timesheet.owner && (
                <Avatar
                  alt={timesheet.owner.firstName && timesheet.owner.lastName
                    ? `${timesheet.owner.firstName} ${timesheet.owner.lastName}`
                    : t('no_data_available')}
                  src={timesheet.owner.avatar || "/static/images/avatar/1.jpg"}
                  sx={{
                    width: "20px",
                    height: "20px",
                  }}
                />
              )}
            </Box>
            {hoveredTimesheetId === timesheet._id && (
              <IconButton
                size="small"
                onClick={(e) => handleDeleteClick(timesheet._id, e)}
                sx={{
                  color: '#666',
                  '&:hover': {
                    color: '#d32f2f',
                  },
                  marginLeft: '8px',
                }}
              >
                <DeleteOutline fontSize="small" />
              </IconButton>
            )}
          </Box>
        ))
      ) : (
        <Box display="flex" justifyContent="center" alignItems="center" height="100%" p={2}>
          <Typography variant="body1" style={{ color: '#777' }}>
            {t('no_data_available')}
          </Typography>
        </Box>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
      >
        <DialogTitle>{t('confirm_delete')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('delete_confirmation_message')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color="primary">
            {t('cancel')}
          </Button>
          <Button onClick={handleConfirmDelete} color="error" autoFocus>
            {t('delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TimesheetSummaryCard; 